import { Text as RNText, TextProps as RNTextProps } from 'react-native';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef } from 'react';
import { cn } from '@/utils/cn';

const textVariants = cva('font-sans', {
  variants: {
    variant: {
      h1: 'text-6xl font-sans-bold text-text-heading',
      h2: 'text-5xl font-sans-bold text-text-heading',
      h3: 'text-4xl font-sans-semibold text-text-heading',
      h4: 'text-3xl font-sans-semibold text-text-heading',
      h5: 'text-2xl font-sans-medium text-text-heading',
      h6: 'text-xl font-sans-medium text-text-heading',
      'body-lg': 'text-lg text-text-body',
      body: 'text-base text-text-body',
      'body-sm': 'text-sm text-text-body',
      caption: 'text-sm text-text-secondary',
      'caption-sm': 'text-xs text-text-secondary',
      code: 'text-sm font-mono text-text-code bg-surface-code px-1 py-0.5 rounded-sm',
      'inline-code':
        'text-sm font-mono text-text-code bg-surface-code px-1 rounded-sm',
      label: 'text-sm font-sans-medium text-text-primary',
      muted: 'text-sm text-text-tertiary',
      disabled: 'text-sm text-text-disabled',
    },
    weight: {
      normal: 'font-sans',
      medium: 'font-sans-medium',
      semibold: 'font-sans-semibold',
      bold: 'font-sans-bold',
    },
    color: {
      primary: 'text-text-primary',
      secondary: 'text-text-secondary',
      tertiary: 'text-text-tertiary',
      heading: 'text-text-heading',
      body: 'text-text-body',
      code: 'text-text-code',
      inverse: 'text-text-inverse',
      disabled: 'text-text-disabled',
      success: 'text-success',
      danger: 'text-danger',
      warning: 'text-warning',
      info: 'text-info',
    },
    align: {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
      justify: 'text-justify',
    },
    truncate: {
      true: 'truncate',
      false: '',
    },
  },
  defaultVariants: {
    variant: 'body',
    align: 'left',
    truncate: false,
  },
});

export interface TextProps
  extends RNTextProps, VariantProps<typeof textVariants> {
  className?: string;
  children: React.ReactNode;
}

const Text = forwardRef<RNText, TextProps>(
  ({ className, variant, weight, color, align, truncate, ...props }, ref) => {
    return (
      <RNText
        ref={ref}
        className={cn(
          textVariants({ variant, weight, color, align, truncate }),
          className
        )}
        {...props}
      />
    );
  }
);

Text.displayName = 'Text';

export { Text, textVariants };
